// - Dynamically fetches product data from Algolia using the query "spiked shoes" and updates the state.
// - Adapts the number of visible products based on screen size using responsive design with `useMediaQuery`.
// - Implements a looping carousel allowing users to navigate through products with left and right buttons.
// - Maps product data into a simplified structure for easier rendering in a reusable `PopularProductTile` component.
// - Displays a loading spinner when products are not yet loaded, ensuring a smooth user experience.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { searchClient } from 'algolia/searchClient';
import Wrapper from 'components/HOC/wrapper';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import Loading from 'components/commercetools-ui/atoms/loading';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, mediumDesktop, tablet, smallMobile, mobile, ultraDesktop } from 'helpers/utils/screensizes';
import { useFormat } from 'helpers/hooks/useFormat';
import { Hit, Hits, offer, ProductObject, swatches } from './type';
import button from 'public/locales/en/product.json';
import useLocale from 'helpers/hooks/useLocale';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import { P2Badge } from '../product/product-list-algolia/types';
import ProductTileComponent from './Tile';

type ProductTileSlider = {
  searchQueryAttribute?: string;
  title?: string;
  contentName?: string;
  shopAllLink?: string;
  p1BadgeData?: any[];
  p2BadgeData?: P2Badge[];
  items?: any;
};

const ProductTileSlider = ({
  searchQueryAttribute,
  title,
  contentName,
  shopAllLink,
  p1BadgeData,
  p2BadgeData,
  items,
}: ProductTileSlider) => {
  const { getCurrency } = useLocale();
  const currency = getCurrency();
  const [indexName, setIndexName] = useState(
    currency === 'GBP'
      ? process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US
      : process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE,
  );

  useEffect(() => {
    setIndexName(
      currency === 'GBP'
        ? process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US
        : process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE,
    );
  }, [currency]);
  const query = searchQueryAttribute;
  const [products, setProducts] = useState<Hits>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const RecommendRef = useRef<HTMLDivElement>(null);
  const [isScrolledToStart, setIsScrolledToStart] = useState(true);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

  const [isultraDesktop] = useMediaQuery(ultraDesktop);
  const [isMediumDesktopSize] = useMediaQuery(mediumDesktop);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTablet] = useMediaQuery(tablet);
  const { formatMessage: formatCommonMessage } = useFormat({ name: 'common' });
  const [startIndex, setStartIndex] = useState(0);
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  const cacheKey = `algolia_${searchQueryAttribute}_${indexName}`;

  const visibleItems = useMemo(() => {
    if (isultraDesktop) return 6;
    if (isMediumDesktopSize) return 5;
    if (isDesktopSize) return 4;
    if (isTablet) return 3;
    return 1.5;
  }, [isultraDesktop, isMediumDesktopSize, isDesktopSize, isTablet]);
  const facetFitersData = query?.split(' AND ')?.map((item) => [item]);
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const cachedData = localStorage.getItem(cacheKey);
        const now = Date.now();
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData);
          if (now - timestamp < ONE_DAY_MS) {
            setProducts(data);
            return;
          }
        }
        const { results } = await searchClient.search({
          requests: [
            {
              indexName: indexName!,
              facetFilters: facetFitersData,
            },
          ],
        });
        const hits = (results?.[0] as unknown as { hits: Hits })?.hits || [];
        setProducts(hits);
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            data: hits,
            timestamp: now,
          }),
        );
      } catch (error) {
        console.error('Error fetching Algolia products:', error);
      }
    };
    if (query) {
      fetchProducts();
    }
  }, [query, indexName, cacheKey]);

  const newArray = products?.map((product: Hit) => ({
    name: currency === 'GBP' ? product?.name?.['en-GB'] : product?.name?.['en-EU'] || product?.name?.['en-GB'],
    image: (product?.images && product?.images?.[0]) || null,
    price: product?.variants?.[0]?.prices || null,
    categoryKey: product?.categoryKeys,
    slug: currency === 'GBP' ? product?.slug?.['en-GB'] : product?.slug?.['en-EU'] || product?.slug?.['en-GB'],
    standalonePrice: product?.standalonePrice,
    offers: (product?.offer as offer) || [],
    categories: product?.categories || [],
    swatches: product?.swatches || [],
    customFit: product?.customFit || '',
    firstReceiptDate: product?.variants?.[0]?.attributes?.firstReceiptDate || '',
    variants: product?.variants || [],
  }));

  const recommendedProducts = items?.map((product: any) => ({
    name: currency === 'GBP' ? product?.name?.['en-GB'] : product?.name?.['en-EU'] || product?.name?.['en-GB'],
    image: (product?.images && product?.images?.[0]) || null,
    price: product?.variants?.[0]?.prices || null,
    categoryKey: product?.categoryKeys,
    slug: currency === 'GBP' ? product?.slug?.['en-GB'] : product?.slug?.['en-EU'] || product?.slug?.['en-GB'],
    standalonePrice: product?.standalonePrice,
    offers: (product?.offer as offer) || [],
    categories: product?.categories || [],
    swatches: product?.swatches || [],
    customFit: product?.customFit || '',
    firstReceiptDate: product?.variants?.[0]?.attributes?.firstReceiptDate || '',
    variants: product?.variants || [],
  }));

  const handleScroll = () => {
    const container = scrollContainerRef.current || RecommendRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setIsScrolledToStart(scrollLeft <= 5);
    setIsScrolledToEnd(scrollLeft + clientWidth >= scrollWidth - 5);
  };

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -scrollContainerRef.current.offsetWidth / visibleItems,
        behavior: 'smooth',
      });

      // Trigger handleScroll manually to update button states
      setTimeout(handleScroll, 300); // Delay to allow smooth scrolling to complete
    }
    if (RecommendRef.current) {
      RecommendRef.current.scrollBy({
        left: -RecommendRef.current.offsetWidth / visibleItems,
        behavior: 'smooth',
      });

      // Trigger handleScroll manually to update button states
      setTimeout(handleScroll, 300); // Delay to allow smooth scrolling to complete
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: scrollContainerRef.current.offsetWidth / visibleItems,
        behavior: 'smooth',
      });

      // Trigger handleScroll manually to update button states
      setTimeout(handleScroll, 300); // Delay to allow smooth scrolling to complete
    }
    if (RecommendRef.current) {
      RecommendRef.current.scrollBy({
        left: RecommendRef.current.offsetWidth / visibleItems,
        behavior: 'smooth',
      });
      // Trigger handleScroll manually to update button states
      setTimeout(handleScroll, 300); // Delay to allow smooth scrolling to complete
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    const recommendContainer = RecommendRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      recommendContainer?.addEventListener('scroll', handleScroll);
      handleScroll(); // Initialize scroll state
    }
    if (recommendContainer) {
      recommendContainer?.addEventListener('scroll', handleScroll);
      handleScroll(); // Initialize scroll state
    }
    return () => {
      container?.removeEventListener('scroll', handleScroll);
      recommendContainer?.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // const renderItems = (items: ProductObject[]) => {
  //   const loopedItems = [...items, ...items.slice(0, visibleItems)];
  //   return loopedItems?.slice(startIndex, startIndex + visibleItems)?.map((item, index: number) => {
  //     return <PopularProductTile product={item} key={index} />;
  //   });
  // };

  // useEffect(() => {
  //   if (startIndex === -1) {
  //     setTimeout(() => {
  //       setStartIndex(products.length - 1);
  //     }, 300);
  //   } else if (startIndex === products.length) {
  //     setTimeout(() => {
  //       setStartIndex(0);
  //     }, 300);
  //   }
  // }, [startIndex, products.length]);

  // const handleRightClick = () => {
  //   setStartIndex((prevIndex) => (prevIndex + 1) % products.length);
  // };

  // const handleLeftClick = () => {
  //   setStartIndex((prevIndex) => (prevIndex - 1 + products.length) % products.length);
  // };

  const handleLeftClick = () => {
    setStartIndex((prevIndex) => Math.max(prevIndex - 1, 0)); // Prevent scrolling past the start
  };

  const handleRightClick = () => {
    setStartIndex((prevIndex) => Math.min(prevIndex + 1, products?.length - Math.ceil(visibleItems))); // Prevent scrolling past the end
  };

  function generateUrl(searchAttribute: string | undefined): string | undefined {
    const parts = searchAttribute?.split(/\s+(AND|OR)\s+/i)?.filter((part) => !/^(AND|OR)$/i?.test(part));

    const values = parts?.map((part) => {
      const afterColon = part?.split(':')[1]?.trim() ?? '';
      return afterColon?.replace(/\s+/g, '+');
    });
    return values?.join('+');
  }

  const renderItems = (items: ProductObject[]) => {
    const itemWidth = 100 / visibleItems;

    return items?.map((item, index) => (
      <div
        key={index}
        className="flex flex-shrink-0 items-stretch transition-transform duration-300"
        style={{
          width: `${itemWidth}%`,
          minWidth: `${itemWidth}%`,
          flex: `0 0 calc(100% / ${visibleItems})`,
          scrollSnapAlign: 'start',
        }}
      >
        <ProductTileComponent product={item} p1BadgeData={p1BadgeData} p2BadgeData={p2BadgeData} />
      </div>
    ));
  };
  const shopAllUrl = useMemo(() => generateUrl(searchQueryAttribute), [searchQueryAttribute]);

  return (
    <div>
      {title ? (
        <>
          {items?.length > 0 && (
            <Wrapper clearDefaultStyles className="homepage-carousels-section px-13 pb-32 pt-16">
              <div
                className={`mb-16 flex items-center ${
                  isDesktopSize || items?.length === 0 ? 'relative justify-center' : 'justify-between pr-10'
                }`}
              >
                <div className="align-center flex justify-center text-center uppercase">
                  <h2 className="font-body text-24 font-bold leading-9 sm:text-36">{title}</h2>
                </div>
                {items?.length > visibleItems && (
                  <div
                    className={`flex items-center justify-end gap-x-6 ${isDesktopSize ? 'absolute right-[2%]' : ''}`}
                  >
                    <button
                      className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-[#e0e0e0] p-3 text-gray-700 hover:bg-[#c0c0c0]
                  ${isScrolledToStart ? 'cursor-not-allowed opacity-50' : ''}`}
                      onClick={handleScrollLeft}
                      disabled={isScrolledToStart}
                      aria-disabled={isScrolledToStart}
                    >
                      <ChevronLeftIcon
                        className="mt-2 h-20 w-20 font-bold text-[#404040]"
                        stroke="currentColor"
                        strokeWidth="3"
                        fill="none"
                      />
                    </button>

                    <button
                      className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-[#e0e0e0] p-3 text-gray-700 hover:bg-[#c0c0c0]
                  ${isScrolledToEnd ? 'cursor-not-allowed opacity-50' : ''}`}
                      onClick={handleScrollRight}
                      disabled={isScrolledToEnd}
                      aria-disabled={isScrolledToEnd}
                    >
                      <ChevronRightIcon
                        className="mt-2 h-20 w-20 font-bold text-[#404040]"
                        stroke="currentColor"
                        strokeWidth="3"
                        fill="none"
                      />
                    </button>
                  </div>
                )}
              </div>

              <div
                ref={RecommendRef}
                className={`ml-4 ${
                  items?.length === 1 ? 'justify-center sm:justify-start 2xl:ml-[4rem]' : ''
                } flex items-stretch overflow-hidden md:ml-0 ${
                  items?.length > 4 ? 'ag__cc__wrapper flex' : 'justify-start 2xl:ml-[4rem]'
                } gap-5`}
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  overflowY: 'hidden',
                  paddingBottom: '10px',
                }}
              >
                {renderItems(recommendedProducts as ProductObject[])}
              </div>
            </Wrapper>
          )}
        </>
      ) : (
        <>
          <Wrapper clearDefaultStyles className="homepage-carousels-section px-13 pb-32 pt-16">
            <div
              className={`mb-16 flex items-center ${
                isDesktopSize || products?.length === 0 ? 'relative justify-center' : 'justify-between pr-10'
              }`}
            >
              <div className="align-center flex justify-center text-center uppercase">
                <h2 className="font-body text-24 font-bold leading-9 sm:text-36">{contentName ?? ''}</h2>
              </div>
              {products?.length > visibleItems && (
                <div className={`flex items-center justify-end gap-x-6 ${isDesktopSize ? 'absolute right-[2%]' : ''}`}>
                  <button
                    className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-[#e0e0e0] p-3 text-gray-700 hover:bg-[#c0c0c0]
                  ${isScrolledToStart ? 'cursor-not-allowed opacity-50' : ''}`}
                    onClick={handleScrollLeft}
                    disabled={isScrolledToStart}
                    aria-disabled={isScrolledToStart}
                  >
                    <ChevronLeftIcon
                      className="mt-2 h-20 w-20 font-bold text-[#404040]"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                    />
                  </button>

                  <button
                    className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-[#e0e0e0] p-3 text-gray-700 hover:bg-[#c0c0c0]
                  ${isScrolledToEnd ? 'cursor-not-allowed opacity-50' : ''}`}
                    onClick={handleScrollRight}
                    disabled={isScrolledToEnd}
                    aria-disabled={isScrolledToEnd}
                  >
                    <ChevronRightIcon
                      className="mt-2 h-20 w-20 font-bold text-[#404040]"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                    />
                  </button>
                </div>
              )}
            </div>
            {products?.length === 0 ? (
              <Loading />
            ) : (
              <>
                <div
                  ref={scrollContainerRef}
                  className={`ml-4 ${
                    products?.length === 1 ? 'justify-center sm:justify-start 2xl:ml-[4rem]' : ''
                  } flex items-stretch overflow-hidden md:ml-0 ${
                    products?.length > 4 ? 'ag__cc__wrapper flex' : 'justify-start 2xl:ml-[4rem]'
                  } gap-5`}
                  style={{
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch',
                    overflowY: 'hidden',
                    paddingBottom: '10px',
                  }}
                >
                  {renderItems(newArray as ProductObject[])}
                </div>

                <div className="mt-8 flex justify-center">
                  <SmartLink href={shopAllLink ? shopAllLink : `/search/${shopAllUrl}`}>
                    <button className="h-[48px] w-[208px] rounded-md bg-[#1dce8f] p-16 text-[16px] font-semibold">
                      {button?.ShopAllButton}
                    </button>
                  </SmartLink>
                </div>
              </>
            )}
          </Wrapper>
        </>
      )}
    </div>
  );
};

export default ProductTileSlider;
